declare global {
    interface Window {
        dataLayer?: Array<Record<string, any>>;
        // 'event' takes an event name; 'set' takes a key and a value (custom tags).
        clarity?: (action: string, ...args: string[]) => void;
    }
}

const SELECTOR = '[data-clarity-event]';

const fire = (target: HTMLElement): void => {
    const eventName = target.dataset.clarityEvent;

    if (!eventName) {
        return;
    }

    // @ts-ignore
    window.clarity?.('event', eventName);
};

const delegate = (event: Event): void => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>(SELECTOR);

    if (target) {
        fire(target);
    }
};

document.addEventListener('click', delegate);
document.addEventListener('auxclick', (event) => {
    if ((event as MouseEvent).button === 1) {
        delegate(event);
    }
});

const SCROLL_MILESTONES = [25, 50, 75, 90];
const firedMilestones = new Set<number>();
let scrollTickScheduled = false;

const checkScrollDepth = (): void => {
    scrollTickScheduled = false;

    const maxScrollable = document.documentElement.scrollHeight - window.innerHeight;

    if (maxScrollable <= 0) {
        return;
    }

    const percent = (window.scrollY / maxScrollable) * 100;

    SCROLL_MILESTONES.forEach((milestone) => {
        if (firedMilestones.has(milestone) || percent < milestone) {
            return;
        }

        firedMilestones.add(milestone);

        const eventName = `scroll_depth_${milestone}`;

        // @ts-ignore
        window.clarity?.('event', eventName);
    });
};

window.addEventListener('scroll', () => {
    if (scrollTickScheduled) {
        return;
    }

    scrollTickScheduled = true;
    requestAnimationFrame(checkScrollDepth);
}, { passive: true });

const TIME_MILESTONES_SECONDS = [30, 60, 90];
const firedTimeMilestones = new Set<number>();

let visibleSeconds = 0;
let lastVisibleAt: number | null = document.visibilityState === 'visible' ? performance.now() : null;

const flushVisibleTime = (): void => {
    if (lastVisibleAt === null) {
        return;
    }

    const now = performance.now();

    visibleSeconds += (now - lastVisibleAt) / 1000;
    lastVisibleAt = now;
};

const checkTimeMilestones = (): void => {
    flushVisibleTime();

    TIME_MILESTONES_SECONDS.forEach((seconds) => {
        if (firedTimeMilestones.has(seconds) || visibleSeconds < seconds) {
            return;
        }

        firedTimeMilestones.add(seconds);

        const eventName = `time_on_page_${seconds}s`;

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'article_event',
            eventCategory: 'engagement',
            eventAction: eventName,
            eventLabel: `${seconds}s`,
        });
    });
};

document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
        lastVisibleAt = performance.now();
    } else {
        flushVisibleTime();
        lastVisibleAt = null;
    }
});

setInterval(checkTimeMilestones, 1000);

const INTERNAL_HOSTNAME_PATTERN = /(^|\.)cybernews\.com$/i;
const AFFILIATE_HOSTNAME_PATTERN = /(^|\.)bi\.cybernews\.com$/i;

type LinkKind = 'affiliate' | 'outbound' | 'internal';

const classifyLink = (link: HTMLAnchorElement): LinkKind | null => {
    if (!link.href) {
        return null;
    }

    let url: URL;

    try {
        url = new URL(link.href);
    } catch {
        return null;
    }

    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return null;
    }

    if (AFFILIATE_HOSTNAME_PATTERN.test(url.hostname)) {
        return 'affiliate';
    }

    if (INTERNAL_HOSTNAME_PATTERN.test(url.hostname)) {
        return 'internal';
    }

    return 'outbound';
};

const fireLinkClassification = (link: HTMLAnchorElement): void => {
    const kind = classifyLink(link);

    if (kind === 'affiliate') {
        // @ts-ignore
        window.clarity?.('event', 'affiliate_click');

        return;
    }

    if (kind === 'outbound') {
        if (link.hasAttribute('data-clarity-event')) {
            return;
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'article_event',
            eventCategory: 'outbound',
            eventAction: 'outbound_click',
            eventLabel: link.href,
        });

        // @ts-ignore
        window.clarity?.('event', 'outbound_click');
    }
};

const delegateLinkClassification = (event: Event): void => {
    const link = (event.target as HTMLElement | null)?.closest<HTMLAnchorElement>('a[href]');

    if (link) {
        fireLinkClassification(link);
    }
};

document.addEventListener('click', delegateLinkClassification);
document.addEventListener('auxclick', (event) => {
    if ((event as MouseEvent).button === 1) {
        delegateLinkClassification(event);
    }
});

export {};
